import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { buildDiffPreview, fireAudit } from '../utils/auditClient';

const NewProgress = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const sportId = queryParams.get('sportId');
  const progressAttributeId = queryParams.get('progressAttributeId');

  const token = localStorage.getItem('auth-token');
  const [heading, setHeading] = useState('');
  const [subHeading, setSubHeading] = useState('');
  const baselineRef = useRef(null);

  const AUTH_HEADER = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };

  const buildSubmitPayload = (h = heading, sh = subHeading) => ({
    id: progressAttributeId || null,
    sportId: sportId ? Number(sportId) : null,
    name: (h || '').trim(),
    description: (sh || '').trim(),
  });

  useEffect(() => {
    if (!progressAttributeId) {
      baselineRef.current = buildSubmitPayload();
      return;
    }

    const fetchData = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_ADMIN_EDIT_PA}/${progressAttributeId}/${sportId}`,
          { method: 'GET', headers: AUTH_HEADER }
        );

        if (!res.ok) throw new Error('Failed to fetch progress attribute');
        const responseData = await res.json();
        const progressData = responseData.data?.sportProgressAttributeEditForm;

        if (progressData) {
          setHeading(progressData.name || '');
          setSubHeading(progressData.description || '');
          baselineRef.current = buildSubmitPayload(progressData.name, progressData.description);
        }
      } catch {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Unable to fetch progress attribute',
          toast: true,
          position: 'top-end',
          timer: 3000,
          showConfirmButton: false,
        });
      }
    };

    fetchData();

  }, [progressAttributeId, sportId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = buildSubmitPayload();

    if (progressAttributeId) {
      const { digest, html } = buildDiffPreview(baselineRef.current || {}, payload);
      if (!digest.length) {
        Swal.fire({
          icon: 'info',
          title: 'Nothing to update',
          text: 'No differences detected.',
          toast: true,
          position: 'top-end',
          timer: 2500,
          showConfirmButton: false,
        });
        return;
      }
      const confirm = await Swal.fire({
        title: 'Apply these changes?',
        html,
        showCancelButton: true,
        confirmButtonText: 'Update',
      });
      if (!confirm.isConfirmed) return;
    } else {
      const { html } = buildDiffPreview({}, payload);
      const confirm = await Swal.fire({
        title: 'Create this progress attribute?',
        html,
        showCancelButton: true,
        confirmButtonText: 'Create',
      });
      if (!confirm.isConfirmed) return;
    }

    const apiUrl = `${process.env.REACT_APP_API_ADMIN_NEW_PROGRESS_ATTRIBUTE}/${sportId}/new-progress-attribute`;

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: AUTH_HEADER,
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      fireAudit({
        entity: 'progress_attribute',
        affected_id: progressAttributeId || null,
        action: progressAttributeId ? 'update' : 'create',
        section: 'SPORT',
        subSection: 'Progress Attribute',
        businessApi: apiUrl,
        businessMethod: 'POST',
        oldPayload: baselineRef.current || {},
        newPayload: payload,
        extra: { page: 'NewProgress' },
      });

      if (response.ok && data.success !== false) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: progressAttributeId
            ? 'Progress attribute updated successfully!'
            : 'Progress attribute created successfully!',
          toast: true,
          position: 'top-end',
          timer: 3000,
          showConfirmButton: false,
        });
        baselineRef.current = payload;
        navigate(-1);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: data.message || 'Failed to create/update progress attribute',
          toast: true,
          position: 'top-end',
          timer: 3000,
          showConfirmButton: false,
        });
      }
    } catch {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An error occurred. Please try again.',
        toast: true,
        position: 'top-end',
        timer: 3000,
        showConfirmButton: false,
      });
    }
  };

  return (
    <div className="enrollment-container">
      <div className="progress-attribute-breadcrumb">
        <Link to="/sportList" className="progress-attribute-breadcrumb-link">
          Sport List
        </Link>{' '}
        &gt; {progressAttributeId ? 'Edit Progress Attribute' : 'New Progress Attribute'}
      </div>

      <form onSubmit={handleSubmit} className="progress-attribute-form">
        <div className="floating-select-form" style={{ width: '98%', position: 'relative' }}>
          <input
            type="text"
            name="heading"
            className={`walkin-form ${heading ? 'has-value' : ''}`}
            value={heading}
            onChange={(e) => setHeading(e.target.value)}
            placeholder="Enter Name"
            required
          />
          {heading && <label htmlFor="heading">Name</label>}
        </div>

        <div className="floating-select-form" style={{ width: '98%', position: 'relative' }}>
          <textarea
            name="subHeading"
            className={`walkin-form ${subHeading ? 'has-value' : ''}`}
            value={subHeading}
            onChange={(e) => setSubHeading(e.target.value)}
            placeholder="Enter Description"
            required
          />
          {subHeading && <label htmlFor="subHeading">Description</label>}
        </div>

        <button type="submit" className="submit-button">
          {progressAttributeId ? 'Update' : 'Submit'}
        </button>
      </form>
    </div>
  );
};

export default NewProgress;
